import he from 'he';
import fp from 'lodash/fp';
import React from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import connect from 'react-redux/lib/connect/connect';
import { LinkContainer } from 'react-router-bootstrap';
import { branch, compose, lifecycle, renderComponent, withProps, withStateHandlers } from "recompose";
import { bindActionCreators } from 'redux';
import { defaultTake, routes } from '../../../../constants';
import { adminActions } from '../../../../state/modules/admin';
import { industriesSelector } from '../../../../state/modules/industry/selectors';
import { noCapMap } from '../../../../utils';
import { ConfirmationModal, LoadingButton, SearchForm, SelectFormField, TextAreaFormField } from '../../../components';
import CalculatingLoading from '../../calculating-loading';
import { DataTable } from './data-table';
import DownloadBusinessActivityButton from './partial/download-business-activity-button-for-admin';

const businessColumns = [
  {
    columnName: 'name',
    display: () => 'business'
  },
  {
    columnName: 'email',
    display: () => 'email'
  },
  {
    columnName: 'subscriptionStatus',
    display: () => 'subscription status'
  },
  {
    columnName: 'options',
    display: () => 'Options'
  }
]

const getDownloadReportButton = (text, businessId, { yearsFromNow }) => (
  <LinkContainer to={routes.adminPortal.businessReport(businessId, yearsFromNow)}>
    <Button bsStyle='plain'>
      {text}
    </Button>
  </LinkContainer>
)

const mapStateToProps = (state) => ({
  industries: industriesSelector(state),
  isFetchingBusiness: state.admin.isFetchingBusiness,
  businessData: state.admin.businessData,
  isUpdatingBusinessNotes: state.admin.isUpdatingBusinessNotes,
  isUpdatingBusinessUploadCpdWithoutApproval: state.admin.isUpdatingBusinessUploadCpdWithoutApproval,
})

const mapDispatchToProps = (dispatch) => ({
  getBusinesses: bindActionCreators(adminActions.admin.getBusinesses, dispatch),
  changeBusinessIndustry: bindActionCreators(adminActions.admin.changeBusinessIndustry, dispatch),
  updateBusinessNotes: bindActionCreators(adminActions.admin.updateBusinessNotes, dispatch),
  updateBusinessUploadCpdWithoutApproval: bindActionCreators(adminActions.admin.updateBusinessUploadCpdWithoutApproval, dispatch),
})

export const BusinessTable = compose(
  connect(mapStateToProps, mapDispatchToProps),
  withStateHandlers(
    ({ industries }) => ({
      searchDebounce: undefined,
      fetchBusinessesQuery: {
        skip: 0,
        take: defaultTake,
        tags: '',
        industryIds: fp.map('id')(industries).join(',')
      }
    }),
    {
      setSelectedIndustry: ({ fetchBusinessesQuery }) => (industryIds) => ({
        fetchBusinessesQuery: {
          ...fetchBusinessesQuery,
          industryIds
        }
      }),
      updateSearchDebounce: () => (searchDebounce) => ({ searchDebounce }),
      onSearchBusinesses: ({ fetchBusinessesQuery }) => (e) => ({
        fetchBusinessesQuery: {
          ...fetchBusinessesQuery,
          skip: 0,
          tags: e.target.value
        }
      }),
      onLoadMoreBusinesses: ({ fetchBusinessesQuery }) => () => ({
        fetchBusinessesQuery: {
          ...fetchBusinessesQuery,
          skip: fetchBusinessesQuery.skip + defaultTake
        }
      })
    }
  ),
  lifecycle({
    componentDidMount() {
      const {
        fetchBusinessesQuery,
        getBusinesses
      } = this.props
      getBusinesses(fetchBusinessesQuery)
    },
    componentDidUpdate(prevProps) {
      const {
        fetchBusinessesQuery,
        getBusinesses,
        searchDebounce,
        updateSearchDebounce,
        isUpdatingBusinessNotes,
        isUpdatingBusinessUploadCpdWithoutApproval,
      } = this.props
      if (!fp.isEqual(prevProps.fetchBusinessesQuery, fetchBusinessesQuery)) {
        const debounced = fp.debounce(500)(
          () => {
            getBusinesses(fetchBusinessesQuery)
          }
        );
        updateSearchDebounce(debounced);
        searchDebounce && searchDebounce.cancel();
        debounced();
      }

      const isUpdatedBusinessNotes = prevProps.isUpdatingBusinessNotes && !isUpdatingBusinessNotes;
      const isUpdatedBusinessUploadCpdWithoutApproval = prevProps.isUpdatingBusinessUploadCpdWithoutApproval && !isUpdatingBusinessUploadCpdWithoutApproval;

      // Refetch the business after the business is updated.
      if (isUpdatedBusinessNotes || isUpdatedBusinessUploadCpdWithoutApproval) {
        getBusinesses(fetchBusinessesQuery);
      }
    }
  }),
  branch(
    ({ businessData }) => !businessData,
    renderComponent(CalculatingLoading)
  ),
  withProps(
    ({ businessData }) => ({
      hasMore: businessData.result.length < businessData.hits
    })
  ),
  withStateHandlers(
    ({ businessData }) => ({
      businesses: fp.flow(
        fp.get('result'),
        fp.map(
          (business) => ({
            ...business,
            isEditingIndustry: false,
            showConfirmation: false,
            nextIndustry: fp.get('industry.id')(fp.first(business.trainingPlans))
          })
        )
      )(businessData)
    }),
    {
      resetData: (state, { businessData }) => () => ({
        businesses: fp.flow(
          fp.get('result'),
          fp.map(
            (business) => ({
              ...business,
              isEditingIndustry: false,
              showConfirmation: false,
              nextIndustry: fp.get('industry.id')(fp.first(business.trainingPlans))
            })
          )
        )(businessData)
      }),
      toggleConfirmation: ({ businesses }) => (id) => ({
        businesses: noCapMap(
          (obj, index) => id === index ? {
            ...obj,
            showConfirmation: !obj.showConfirmation,
          } : obj
        )(businesses)
      }),
      toggleIndustryEdit: ({ businesses }) => (id) => ({
        businesses: noCapMap(
          (obj, index) => id === index ? {
            ...obj,
            isEditingIndustry: !obj.isEditingIndustry,
            nextIndustry: fp.get('industry.id')(fp.first(obj.trainingPlans))
          } : obj
        )(businesses)
      }),
      updateNextIndustry: ({ businesses }) => (id, nextIndustry) => ({
        businesses: noCapMap(
          (obj, index) => id === index ? { ...obj, nextIndustry } : obj
        )(businesses)
      })
    }
  ),
  lifecycle({
    componentWillReceiveProps(nextProps) {
      const {
        isFetchingBusiness,
      } = this.props
      if (isFetchingBusiness && !nextProps.isFetchingBusiness) {
        nextProps.resetData()
      }
    }
  }),
  withStateHandlers(
    {
      // The business id that is selected to show on the details modal.
      selectedBusinessId: null,
      businessNotesInput: '',
      selectedAllowUploadCpdWithoutApproval: '',
    },
    {
      toggleDetailsModal: ({ selectedBusinessId }) => (business) => ({
        selectedBusinessId: selectedBusinessId === business.id ? null : business.id,
        businessNotesInput: selectedBusinessId === business.id ? '' : business.notes || '',
        selectedAllowUploadCpdWithoutApproval: selectedBusinessId === business.id ? '' : business.uploadCpdWithoutApproval,
      }),
      updateBusinessNotesInput: () => (nextInput) => ({
        businessNotesInput: nextInput,
      }),
      updateSelectedAllowUploadCpdWithoutApproval: () => (nextSelect) => ({
        selectedAllowUploadCpdWithoutApproval: nextSelect,
      }),
    }
  ),
  withProps(
    ({
      businesses,
      addTasks,
      toggleConfirmation,
      toggleIndustryEdit,
      updateNextIndustry,
      changeBusinessIndustry,
      industries,
      handleBusinessQueryUpdate,
      toggleDetailsModal,
      selectedBusinessId,
      businessNotesInput,
      updateBusinessNotesInput,
      isUpdatingBusinessNotes,
      updateBusinessNotes,
      updateBusinessUploadCpdWithoutApproval,
      isUpdatingBusinessUploadCpdWithoutApproval,
      selectedAllowUploadCpdWithoutApproval,
      updateSelectedAllowUploadCpdWithoutApproval,
    }) => ({
      className: 'business-table',
      rows: fp.flow(
        noCapMap(
          ({ business, subscription, trainingPlans, isEditingIndustry, showConfirmation, nextIndustry }, id) => ({
            ...business,
            name: <div>
              <Button bsStyle='plain' className='no-padding' onClick={() => handleBusinessQueryUpdate(business)}>{business.name}</Button>
            </div>,
            subscriptionStatus: subscription ? subscription.status : "no subscription",
            options: <div>
              <Button onClick={() => toggleDetailsModal(business)}>Details</Button>
              <ConfirmationModal
                className="update-business-details-modal"
                show={selectedBusinessId === business.id}
                cancel={() => toggleDetailsModal(business)}
                actions={[]}
                cancelLabel='Back'
                message={
                  <form className='top-gap'>
                    <div className='top-gap flexbox align-center industry'>
                      <span className='title'>Industry:</span>
                      <SelectFormField
                        emptyOption={false}
                        value={nextIndustry}
                        onChange={(e) => updateNextIndustry(id, parseInt(e.target.value, 10))}>
                        {
                          fp.map(({ id, name }) =>
                            <option key={id} value={id}>{name}</option>
                          )(industries)
                        }
                      </SelectFormField>
                      <Button disabled={fp.flow(
                        fp.map('industry.id'),
                        fp.contains(nextIndustry)
                      )(trainingPlans)} onClick={() => {
                        changeBusinessIndustry(business.id, nextIndustry)
                      }}>Update</Button>
                    </div>
                    <div className='top-gap flexbox align-center reports'>
                      <span className='title'>Reports:</span>
                      <span>
                        {
                          getDownloadReportButton('Current Period', business.id, { yearsFromNow: 0 })
                        }
                      </span>
                      <span>
                        {
                          getDownloadReportButton('Last Period', business.id, { yearsFromNow: -1 })
                        }
                      </span>
                      <span>
                        <DownloadBusinessActivityButton 
                          business={business}
                          trainingPlans={trainingPlans}
                          buttonText='Export CPD Activities'
                          downloadFor='admin'
                        />
                      </span>
                    </div>
                    <div className='top-gap flexbox align-center'>
                      <span className='title'>User upload CPD without approval:</span>
                      <SelectFormField
                        emptyOption={false}
                        value={selectedAllowUploadCpdWithoutApproval}
                        onChange={
                          (e) => updateSelectedAllowUploadCpdWithoutApproval(e.target.value === 'true')
                        }
                        selectStyle={{ minWidth: '50px' }}
                      >
                        {
                          fp.map((value) =>
                            <option key={value} value={value}>{value === true ? "Yes" : "No"}</option>
                          )([true, false])
                        }
                      </SelectFormField>
                      <LoadingButton
                        text='Save'
                        disabled={selectedAllowUploadCpdWithoutApproval === business.uploadCpdWithoutApproval}
                        loading={isUpdatingBusinessUploadCpdWithoutApproval}
                        onClick={() => updateBusinessUploadCpdWithoutApproval(business.id, selectedAllowUploadCpdWithoutApproval)}
                      />
                    </div>
                    <div className='top-gap flexbox align-center'>
                      <span className='title'>Notes:</span>
                      <TextAreaFormField
                        inputProps={{ style: { width: '400px' }}}
                        rows={1}
                        value={businessNotesInput}
                        onChange={(e) => updateBusinessNotesInput(e.target.value)}
                      />
                      <LoadingButton
                        text='Save'
                        disabled={(business.notes || '') === businessNotesInput}
                        loading={isUpdatingBusinessNotes}
                        onClick={() => updateBusinessNotes(business.id, businessNotesInput)}
                      />
                    </div>
                  </form>
                }
                headingText={
                  <div className='flexbox justify-between align-center'>
                    <span>{business.name} Details</span>
                  </div>
                } />
            </div>
          }),
        )
      )(businesses),
      columns: businessColumns
    })
  )
)(
  ({ onSearchBusinesses, fetchBusinessesQuery, isFetchingBusiness, hasMore, onLoadMoreBusinesses, setSelectedIndustry, industries, ...props }) => {
    return <div>
      <Row className='no-gutter top-gap'>
        <Col md={3} sm={12}>
          <SearchForm
            label="Search businesses"
            searchQuery={fetchBusinessesQuery.tags}
            onSearchChange={onSearchBusinesses}
          />
        </Col>
        <Col md={3} sm={12} mdOffset={1}>
          <form>
            <SelectFormField
              emptyOption={true}
              label='Select industry'
              onChange={(e) => {
                const industryId = parseInt(e.target.value, 10)
                if (!industryId) {
                  const allIndustryIds = fp.map('id')(industries).join(',')
                  setSelectedIndustry(allIndustryIds)
                } else {
                  setSelectedIndustry(industryId)
                }
              }}>
              {
                fp.map(
                  ({ id, name }) => <option key={id} value={id}>{he.decode(name)}</option>
                )(industries)
              }
            </SelectFormField>
          </form>
        </Col>
      </Row>
      <DataTable {...{...props, isLoading: isFetchingBusiness}} />
      {
        hasMore ?
          <LoadingButton text='Load More' loading={isFetchingBusiness} onClick={onLoadMoreBusinesses} /> :
          null
      }
    </div>
  }
)


// WEBPACK FOOTER //
// ./src/containers/pages/admin-portal/user-portal/business-table.js